/* =========================
   maintain.js (FULL FILE) - GPU OPTIMIZED RAIL
   ========================= */

/* -----------------------------
   Helpers
------------------------------ */
function rafOnce(fn) {
  requestAnimationFrame(() => requestAnimationFrame(fn));
}

/* -----------------------------
   GALLERY INIT (Landing)
------------------------------ */
function initMaintainGallery(root = document) {
  const gallery = root.querySelector(".gallery");
  if (!gallery) return;

  if (gallery.__maintainGalleryInit) return;
  gallery.__maintainGalleryInit = true;

  const modeButtons = root.querySelectorAll(
    ".filter-item[data-filter='item'], .filter-item[data-filter='model']"
  );
  const refineButton = root.querySelector(".filter-item[data-filter='refine']");
  const refinePanel = root.querySelector(".refine-panel");
  const refineClose = root.querySelector(".refine-close");
  const refineCheckboxes = root.querySelectorAll(".refine-checkbox");
  const clearButton = root.querySelector(".refine-clear");
  const productImages = root.querySelectorAll(".product__image");
  const products = Array.from(root.querySelectorAll(".product"));

  const setMode = (mode) => {
    productImages.forEach((img) => img.classList.add("is-fading"));

    setTimeout(() => {
      gallery.classList.toggle("gallery--mode-item", mode === "item");
      gallery.classList.toggle("gallery--mode-model", mode === "model");

      modeButtons.forEach((btn) => {
        const isActive = btn.dataset.filter === mode;
        btn.classList.toggle("is-active", isActive);
        btn.setAttribute("aria-pressed", String(isActive));
      });

      productImages.forEach((img) => img.classList.remove("is-fading"));
    }, 180);
  };

  modeButtons.forEach((button) => {
    button.addEventListener("click", () => {
      const mode = button.dataset.filter;
      if (mode === "item" || mode === "model") setMode(mode);
    });
  });

  setMode("item");

  const setRefineOpen = (open) => {
    if (!refinePanel) return;

    refinePanel.classList.toggle("is-open", open);
    refinePanel.setAttribute("aria-hidden", String(!open));

    if (refineButton) {
      refineButton.classList.toggle("is-active", open);
      refineButton.setAttribute("aria-pressed", String(open));
    }

    refineCheckboxes.forEach((cb) => (cb.disabled = !open));
  };

  setRefineOpen(false);

  let lastRefinePointerDownAt = 0;

  const toggleRefine = (e) => {
    if (!refineButton || !refinePanel) return;

    if (e) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();
    }

    const next = !refinePanel.classList.contains("is-open");
    setRefineOpen(next);
  };

  if (refineButton && refinePanel) {
    refineButton.addEventListener(
      "pointerdown",
      (e) => {
        lastRefinePointerDownAt = performance.now();
        toggleRefine(e);
      },
      { passive: false }
    );

    refineButton.addEventListener(
      "click",
      (e) => {
        const now = performance.now();
        if (now - lastRefinePointerDownAt < 550) {
          e.preventDefault();
          e.stopPropagation();
          if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();
          return;
        }
        toggleRefine(e);
      },
      { passive: false }
    );
  }

  const stopInside = (e) => {
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();
  };
  if (refinePanel) {
    refinePanel.addEventListener("pointerdown", stopInside, { passive: true });
    refinePanel.addEventListener("click", stopInside, { passive: true });
    refinePanel.addEventListener("touchstart", stopInside, { passive: true });
  }

  if (refineClose) {
    refineClose.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();
      setRefineOpen(false);
    });
  }

  const onDocPointerDown = (e) => {
    if (!refinePanel || !refinePanel.classList.contains("is-open")) return;

    const t = e.target;
    if (!t) return;

    if (refineButton && refineButton.contains(t)) return;
    if (refinePanel.contains(t)) return;

    setRefineOpen(false);
  };

  document.addEventListener("pointerdown", onDocPointerDown, true);
  document.addEventListener("click", onDocPointerDown, true);

  gallery.__cleanupRefine = () => {
    document.removeEventListener("pointerdown", onDocPointerDown, true);
    document.removeEventListener("click", onDocPointerDown, true);
  };

  const applyFilters = () => {
    const active = Array.from(refineCheckboxes)
      .filter((cb) => cb.checked)
      .map((cb) => cb.value.toLowerCase());

    products.forEach((p) => p.classList.add("is-fading"));

    setTimeout(() => {
      products.forEach((product) => {
        const tags = (product.dataset.tags || "").toLowerCase();
        const visible = active.length === 0 || active.some((c) => tags.includes(c));
        product.style.display = visible ? "" : "none";
      });
      products.forEach((p) => p.classList.remove("is-fading"));
    }, 180);
  };

  refineCheckboxes.forEach((cb) => cb.addEventListener("change", applyFilters));
  if (clearButton) {
    clearButton.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();
      refineCheckboxes.forEach((cb) => (cb.checked = false));
      applyFilters();
    });
  }
}

/* -----------------------------
   HEADER NAVIGATION / SCROLL
------------------------------ */
function initMaintainHeader(root = document) {
  const topBar = root.querySelector(".top-bar");
  const primaryNavLinks = root.querySelectorAll(".nav-link[data-nav-role='primary']");

  if (primaryNavLinks.length) primaryNavLinks[0].classList.add("is-active");

  primaryNavLinks.forEach((link) => {
    link.addEventListener("click", (event) => {
      event.preventDefault();
      primaryNavLinks.forEach((navItem) => navItem.classList.remove("is-active"));
      link.classList.add("is-active");
    });
  });

  const updateHeaderOpacity = () => {
    if (!topBar) return;
    topBar.classList.toggle("is-scrolled", window.scrollY > 0);
  };

  updateHeaderOpacity();
  window.addEventListener("scroll", updateHeaderOpacity, { passive: true });
}

/* ====================================================
   PRODUCT PAGE INIT
==================================================== */
function initMaintainProduct(root = document) {
  const section = root.querySelector(".maintain-product");
  if (!section) return;

  if (section.__maintainProductInit) return;
  section.__maintainProductInit = true;

  const tabButtons = Array.from(section.querySelectorAll(".product-tab"));
  tabButtons.forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.preventDefault();

      const name = btn.dataset.tab;
      const panel = section.querySelector(
        `.product-tab-panel[data-tab-panel="${name}"]`
      );
      if (!panel) return;

      const isOpen = btn.classList.contains("is-active");
      btn.classList.toggle("is-active", !isOpen);
      panel.classList.toggle("is-active", !isOpen);
    });
  });

  const sizeChartTriggers = Array.from(
    section.querySelectorAll("[data-size-chart-trigger]")
  );
  sizeChartTriggers.forEach((trigger) => {
    const wrapper = trigger
      .closest(".product-tab-panel")
      ?.querySelector("[data-size-chart-wrapper]");
    if (!wrapper) return;

    trigger.addEventListener("click", (e) => {
      e.preventDefault();
      wrapper.classList.toggle("is-open");
    });
  });

  const slides = Array.from(section.querySelectorAll(".product-media-slide"));
  const dots = Array.from(section.querySelectorAll(".product-dot"));
  const leftBtn = section.querySelector(".product-arrow--left");
  const rightBtn = section.querySelector(".product-arrow--right");
  const viewport = section.querySelector(".product-media__viewport");

  if (!slides.length) return;

  let index = Math.max(
    0,
    slides.findIndex((s) => s.classList.contains("is-active"))
  );

  const render = (i) => {
    index = (i + slides.length) % slides.length;
    slides.forEach((s, k) => s.classList.toggle("is-active", k === index));
    dots.forEach((d, k) => d.classList.toggle("is-active", k === index));
  };

  if (leftBtn) {
    leftBtn.addEventListener("click", (e) => {
      e.preventDefault();
      render(index - 1);
    });
  }
  if (rightBtn) {
    rightBtn.addEventListener("click", (e) => {
      e.preventDefault();
      render(index + 1);
    });
  }

  dots.forEach((dot) => {
    dot.addEventListener("click", (e) => {
      e.preventDefault();
      const i = Number(dot.getAttribute("data-dot-index"));
      if (!Number.isNaN(i)) render(i);
    });
  });

  section.addEventListener("keydown", (e) => {
    if (e.key === "ArrowLeft") render(index - 1);
    if (e.key === "ArrowRight") render(index + 1);
  });

  if (viewport) {
    let startX = 0,
      startY = 0,
      active = false;

    viewport.addEventListener(
      "touchstart",
      (e) => {
        const t = e.touches && e.touches[0];
        if (!t) return;
        active = true;
        startX = t.clientX;
        startY = t.clientY;
      },
      { passive: true }
    );

    viewport.addEventListener(
      "touchend",
      (e) => {
        if (!active) return;
        active = false;

        const t = e.changedTouches && e.changedTouches[0];
        if (!t) return;

        const dx = t.clientX - startX;
        const dy = t.clientY - startY;

        if (Math.abs(dx) < 40) return;
        if (Math.abs(dx) < Math.abs(dy)) return;

        if (dx < 0) render(index + 1);
        else render(index - 1);
      },
      { passive: true }
    );
  }
}

/* ====================================================
   MAINTAIN PRODUCT RAIL – GPU ACCELERATED (transform)
==================================================== */
function initMaintainRail(root = document) {
  const rails = Array.from(root.querySelectorAll(".maintain-product-rail"));
  if (!rails.length) return;

  rails.forEach((rail) => {
    if (rail.__cleanup) rail.__cleanup();

    const viewport = rail.querySelector(
      ".maintain-product-rail__viewport, [data-rail-viewport]"
    );
    const track = rail.querySelector(".maintain-product-rail__track, [data-rail-track]");
    const scrollbarTrack = rail.querySelector(".maintain-product-rail__scrollbar-track");
    const scrollbarThumb = rail.querySelector(".maintain-product-rail__scrollbar-thumb");
    
    if (!viewport || !track || !scrollbarTrack || !scrollbarThumb) return;

    // Store original items
    if (!track.__baseItems) {
      track.__baseItems = Array.from(track.children).map((n) => n.cloneNode(true));
    }

    const pxPerSec = (Number(rail.dataset.scrollSpeed) || 3) * 20;
    let totalWidth = 0;
    let singleSetWidth = 0;
    let currentX = 0;
    let enabled = true;
    let rafId = 0;
    let lastT = 0;

    let resumeTimer = 0;
    const RESUME_MS = 300;

    const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

    const setEnabled = (on) => {
      enabled = !!on;
      if (enabled) lastT = performance.now();
    };

    const pauseFor = (ms) => {
      setEnabled(false);
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => setEnabled(true), ms);
    };

    const syncScrollbar = () => {
      if (!singleSetWidth) return;
      const max = scrollbarTrack.clientWidth - scrollbarThumb.clientWidth;
      if (max <= 0) return;
      const ratio = clamp((currentX % singleSetWidth) / singleSetWidth, 0, 1);
      scrollbarThumb.style.left = ratio * max + "px";
    };

    const applyTransform = () => {
      track.style.transform = `translate3d(${-currentX}px, 0, 0)`;
    };

    const buildLoopedTrack = () => {
      track.innerHTML = "";
      
      // Add items twice for seamless loop
      track.__baseItems.forEach((n) => track.appendChild(n.cloneNode(true)));
      track.__baseItems.forEach((n) => track.appendChild(n.cloneNode(true)));

      if (!track.children.length) {
        totalWidth = 0;
        singleSetWidth = 0;
        return;
      }

      rafOnce(() => {
        totalWidth = track.scrollWidth;
        singleSetWidth = totalWidth / 2;
        currentX = 0;
        applyTransform();
        syncScrollbar();
      });
    };

    // GPU-accelerated animation loop
    const loop = (t) => {
      if (!lastT) lastT = t;
      const dt = (t - lastT) / 1000;
      lastT = t;

      if (enabled && singleSetWidth > 0) {
        currentX += pxPerSec * dt;
        
        // Seamless loop reset
        if (currentX >= singleSetWidth) {
          currentX -= singleSetWidth;
        }
        
        applyTransform();
        syncScrollbar();
      }

      rafId = requestAnimationFrame(loop);
    };

    // Hover pause
    let overItem = false;

    const isOverItem = (target) =>
      !!(target && target.closest && target.closest(".maintain-product-rail__item"));

    const onPointerOver = (e) => {
      if (e.pointerType && e.pointerType !== "mouse" && e.pointerType !== "pen") return;
      if (!isOverItem(e.target)) return;
      if (overItem) return;
      overItem = true;
      setEnabled(false);
    };

    const onPointerOut = (e) => {
      if (e.pointerType && e.pointerType !== "mouse" && e.pointerType !== "pen") return;
      if (!overItem) return;

      const fromItem = e.target?.closest?.(".maintain-product-rail__item");
      const toItem = e.relatedTarget?.closest?.(".maintain-product-rail__item");

      if (fromItem && toItem && fromItem === toItem) return;

      if (!toItem) {
        overItem = false;
        setEnabled(true);
      }
    };

    rail.addEventListener("pointerover", onPointerOver, { passive: true });
    rail.addEventListener("pointerout", onPointerOut, { passive: true });

    // Touch handling
    let touchStartX = 0;
    let touchStartCurrentX = 0;

    const onTouchStart = (e) => {
      const t = e.touches?.[0];
      if (!t) return;
      
      setEnabled(false);
      clearTimeout(resumeTimer);
      
      touchStartX = t.clientX;
      touchStartCurrentX = currentX;
    };

    const onTouchMove = (e) => {
      const t = e.touches?.[0];
      if (!t) return;
      
      const dx = touchStartX - t.clientX;
      currentX = Math.max(0, touchStartCurrentX + dx);
      
      if (currentX >= singleSetWidth) {
        currentX -= singleSetWidth;
        touchStartCurrentX -= singleSetWidth;
      } else if (currentX < 0) {
        currentX += singleSetWidth;
        touchStartCurrentX += singleSetWidth;
      }
      
      applyTransform();
      syncScrollbar();
    };

    const onTouchEnd = () => {
      pauseFor(RESUME_MS);
    };

    rail.addEventListener("touchstart", onTouchStart, { passive: true });
    rail.addEventListener("touchmove", onTouchMove, { passive: true });
    rail.addEventListener("touchend", onTouchEnd, { passive: true });
    rail.addEventListener("touchcancel", onTouchEnd, { passive: true });

    // Scrollbar drag
    let dragging = false;
    let dragStartX = 0;
    let dragStartLeft = 0;

    const startDrag = (e) => {
      dragging = true;
      setEnabled(false);
      scrollbarThumb.classList.add("is-dragging");
      dragStartX = e.clientX || e.touches?.[0]?.clientX || 0;
      dragStartLeft = parseFloat(getComputedStyle(scrollbarThumb).left) || 0;
    };

    const moveDrag = (e) => {
      if (!dragging) return;
      const x = e.clientX || e.touches?.[0]?.clientX || 0;
      const max = scrollbarTrack.clientWidth - scrollbarThumb.clientWidth;
      if (max <= 0 || singleSetWidth <= 0) return;

      const left = clamp(dragStartLeft + (x - dragStartX), 0, max);
      scrollbarThumb.style.left = left + "px";

      currentX = (left / max) * singleSetWidth;
      applyTransform();
    };

    const endDrag = () => {
      dragging = false;
      scrollbarThumb.classList.remove("is-dragging");
      pauseFor(260);
    };

    scrollbarThumb.addEventListener("mousedown", startDrag);
    document.addEventListener("mousemove", moveDrag);
    document.addEventListener("mouseup", endDrag);

    scrollbarThumb.addEventListener("touchstart", startDrag, { passive: true });
    document.addEventListener("touchmove", moveDrag, { passive: true });
    document.addEventListener("touchend", endDrag, { passive: true });

    // Resize handler
    const resize = () => {
      const ratio = singleSetWidth > 0 ? (currentX % singleSetWidth) / singleSetWidth : 0;
      buildLoopedTrack();
      rafOnce(() => {
        if (singleSetWidth > 0) {
          currentX = ratio * singleSetWidth;
          applyTransform();
        }
        syncScrollbar();
      });
    };

    const ro = new ResizeObserver(resize);
    ro.observe(viewport);

    // Initialize
    buildLoopedTrack();
    lastT = performance.now();
    rafId = requestAnimationFrame(loop);

    // Cleanup
    rail.__cleanup = () => {
      cancelAnimationFrame(rafId);
      clearTimeout(resumeTimer);
      ro.disconnect();

      rail.removeEventListener("pointerover", onPointerOver);
      rail.removeEventListener("pointerout", onPointerOut);

      rail.removeEventListener("touchstart", onTouchStart);
      rail.removeEventListener("touchmove", onTouchMove);
      rail.removeEventListener("touchend", onTouchEnd);
      rail.removeEventListener("touchcancel", onTouchEnd);

      document.removeEventListener("mousemove", moveDrag);
      document.removeEventListener("mouseup", endDrag);
      document.removeEventListener("touchmove", moveDrag);
      document.removeEventListener("touchend", endDrag);
    };
  });
}

/* -----------------------------
   BOOTSTRAP
------------------------------ */
document.addEventListener("DOMContentLoaded", () => {
  initMaintainHeader(document);
  initMaintainGallery(document);
  initMaintainProduct(document);
  initMaintainRail(document);
});

window.addEventListener("pageshow", () => {
  initMaintainGallery(document);
  initMaintainProduct(document);
  initMaintainRail(document);
});

document.addEventListener("shopify:section:load", (e) => {
  initMaintainGallery(e.target);
  initMaintainProduct(e.target);
  initMaintainRail(e.target);
});